import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';

import { ThemeProvider } from '@/context/ThemeContext';
import { SessionProvider } from '@/context/SessionContext';
import { SupportSessionProvider } from '@/context/SupportSessionContext';
import AuthGuard from '@/components/dashboard/AuthGuard';
import ApiErrorModal from '@/components/dashboard/ApiErrorModal';
import NetworkStatusBar from '@/components/dashboard/NetworkStatusBar';
import ChunkErrorBoundary from '@/components/dashboard/ChunkErrorBoundary';
import '@/App.css';

// Route-level code splitting — keeps the initial bundle lean.
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const DashboardLayout = lazy(() => import('@/components/dashboard/DashboardLayout'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const ModulePlaceholder = lazy(() => import('@/pages/dashboard/ModulePlaceholder'));
const StockDetailsPage = lazy(() => import('@/pages/dashboard/reports/StockDetailsPage'));
const ItemLedgerPage = lazy(() => import('@/pages/dashboard/reports/ItemLedgerPage'));
const PurchaseSalesSummaryPage = lazy(() => import('@/pages/dashboard/reports/PurchaseSalesSummaryPage'));
const InvoiceB2CListPage = lazy(() => import('@/pages/dashboard/reports/InvoiceB2CListPage'));
const InvoiceB2BListPage = lazy(() => import('@/pages/dashboard/reports/InvoiceB2BListPage'));
const SalesReturnB2CListPage = lazy(() => import('@/pages/dashboard/reports/SalesReturnB2CListPage'));
const SalesReturnB2BListPage = lazy(() => import('@/pages/dashboard/reports/SalesReturnB2BListPage'));
const ImeiStockDetailsPage = lazy(() => import('@/pages/dashboard/reports/ImeiStockDetailsPage'));
const StandardVoucherEntryPage = lazy(() => import('@/pages/dashboard/accounts/StandardVoucherEntryPage'));
const MoneyReceiptB2BPage = lazy(() => import('@/pages/dashboard/accounts/MoneyReceiptB2BPage'));
const PaymentB2BPage = lazy(() => import('@/pages/dashboard/accounts/PaymentB2BPage'));
const AccountLedgerPage = lazy(() => import('@/pages/dashboard/accounts/AccountLedgerPage'));
const AccountMasterPage = lazy(() => import('@/pages/dashboard/accounts/AccountMasterPage'));
const ItemMasterPage = lazy(() => import('@/pages/dashboard/master/ItemMasterPage'));
const PartyMasterPage = lazy(() => import('@/pages/dashboard/master/PartyMasterPage'));
const SalesOrderB2BPage = lazy(() => import('@/pages/dashboard/inventory/SalesOrderB2BPage'));
const InvoiceB2BPage    = lazy(() => import('@/pages/dashboard/inventory/InvoiceB2BPage'));
const PrintTemplateDesignerPage = lazy(() => import('@/pages/dashboard/tools/PrintTemplateDesignerPage'));
const PrintingFormatSettingsPage = lazy(() => import('@/pages/dashboard/tools/PrintingFormatSettingsPage'));
const ConnectSupportPage = lazy(() => import('@/pages/support/ConnectSupportPage'));

function RouteFallback() {
  return (
    <div className="flex min-h-[100dvh] w-full items-center justify-center bg-background text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
    </div>
  );
}

// Placeholder module wrappers — routes exist so the menu items work today.
// Each real module will replace one of these lazy imports later.
const InventorySalesOrderB2B = () => <SalesOrderB2BPage />;
const InventoryInvoiceB2B    = () => <InvoiceB2BPage />;
const AccountsMoneyReceiptB2B = () => <MoneyReceiptB2BPage />;
const AccountsStandardVoucherEntry = () => <StandardVoucherEntryPage />;
const ReportsStockDetails = () => <StockDetailsPage />;
const ReportsItemLedger = () => <ItemLedgerPage />;
const ReportsPurchaseSalesSummary = () => <PurchaseSalesSummaryPage />;
const ReportsInvoiceB2CList = () => <InvoiceB2CListPage />;
const ReportsInvoiceB2BList = () => <InvoiceB2BListPage />;
const ReportsSalesReturnB2CList = () => <SalesReturnB2CListPage />;
const ReportsSalesReturnB2BList = () => <SalesReturnB2BListPage />;
const ReportsImeiStock = () => <ImeiStockDetailsPage />;
const GstGstr1 = () => (
  <ModulePlaceholder
    id="gst-gstr1"
    title="GSTR 1"
    description="Prepare, reconcile and file GSTR-1 (Outward Supplies) for the active company and FY."
  />
);
const MasterItem = () => <ItemMasterPage />;

function App() {
  return (
    <ThemeProvider defaultTheme="system">
      <SessionProvider>
        <SupportSessionProvider>
        <BrowserRouter>
        <ChunkErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            <Route
              path="/dashboard"
              element={
                <AuthGuard>
                  <DashboardLayout />
                </AuthGuard>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="inventory/sales-order-b2b" element={<InventorySalesOrderB2B />} />
              <Route path="inventory/invoice-b2b"     element={<InventoryInvoiceB2B />} />
              <Route path="accounts/money-receipt-b2b" element={<AccountsMoneyReceiptB2B />} />
              <Route path="accounts/payment-b2b" element={<PaymentB2BPage />} />
              <Route path="accounts/standard-voucher-entry" element={<AccountsStandardVoucherEntry />} />
              <Route path="accounts/account-ledger" element={<AccountLedgerPage />} />
              <Route path="accounts/account-master" element={<AccountMasterPage />} />
              <Route path="reports/stock-details" element={<ReportsStockDetails />} />
              <Route path="reports/item-ledger" element={<ReportsItemLedger />} />
              <Route path="reports/purchase-sales-summary" element={<ReportsPurchaseSalesSummary />} />
              <Route path="reports/invoice-b2c-list" element={<ReportsInvoiceB2CList />} />
              <Route path="reports/invoice-b2b-list" element={<ReportsInvoiceB2BList />} />
              <Route path="reports/salesreturn-b2c-list" element={<ReportsSalesReturnB2CList />} />
              <Route path="reports/salesreturn-b2b-list" element={<ReportsSalesReturnB2BList />} />
              <Route path="reports/imei-stock-details" element={<ReportsImeiStock />} />
              <Route path="gst/gstr-1" element={<GstGstr1 />} />
              <Route path="master/item" element={<MasterItem />} />
              <Route path="master/party" element={<PartyMasterPage />} />
              <Route path="tools/print-template-designer" element={<PrintTemplateDesignerPage />} />
              <Route path="tools/printing-format-settings" element={<PrintingFormatSettingsPage />} />
              <Route path="help/connect-support" element={<ConnectSupportPage />} />
            </Route>

            {/* Public support entry — reachable from the login page. */}
            <Route path="/support" element={<ConnectSupportPage standalone />} />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/home" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
        </ChunkErrorBoundary>
        <Toaster
          position="top-right"
          richColors
          closeButton
          offset={80}
          toastOptions={{
            className:
              'font-body border border-border bg-card text-card-foreground shadow-lg',
          }}
        />
        {/* Central Action-API Error Modal — PRD Universality Rule #15 */}
        <ApiErrorModal />
        {/* Global Network Status bar + offline body-class — PRD Universality Rule #16 */}
        <NetworkStatusBar />
      </BrowserRouter>
      </SupportSessionProvider>
      </SessionProvider>
    </ThemeProvider>
  );
}

export default App;
